/**
 * Locale-aware formatters used throughout the app and invoice PDFs.
 *
 *   Locale     Currency       Date                Number
 *   ────────── ────────────── ─────────────────── ───────────────────
 *   fr-FR      1 234,56 €     12 mai 2026         1 234,56
 *   en-GB      €1,234.56      12 May 2026         1,234.56
 *
 * We standardise on EUR everywhere since UNIVERS COSMETIC operates in France.
 */

const LOCALE_MAP = {
    fr: 'fr-FR',
    en: 'en-GB',
};

const toBcp47 = (lang) => LOCALE_MAP[(lang || '').slice(0, 2)] || LOCALE_MAP.fr;

export const formatCurrency = (amount, lang = 'fr', currency = 'EUR') => {
    const value = Number(amount || 0);
    try {
        return new Intl.NumberFormat(toBcp47(lang), {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${value.toFixed(2)} €`;
    }
};

export const formatNumber = (amount, lang = 'fr', maxFractionDigits = 2) => {
    try {
        return new Intl.NumberFormat(toBcp47(lang), {
            maximumFractionDigits: maxFractionDigits,
        }).format(Number(amount || 0));
    } catch {
        return String(amount);
    }
};

export const formatDate = (iso, lang = 'fr', withTime = true) => {
    if (!iso) return '';
    try {
        const opts = {
            day: '2-digit', month: 'short', year: 'numeric',
            ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        };
        return new Date(iso).toLocaleString(toBcp47(lang), opts);
    } catch {
        return String(iso);
    }
};

export const formatDateOnly = (iso, lang = 'fr') => formatDate(iso, lang, false);

/**
 * Format a VAT/TVA percentage value. French style uses a NBSP before the % sign
 * (e.g. "20 %"); English style is "20%".
 */
export const formatVat = (rate, lang = 'fr') => {
    const n = Number(rate || 0);
    return toBcp47(lang).startsWith('fr')
        ? `${n}\u00a0%`
        : `${n}%`;
};
